<template>
  <BCard>
    <div class="text-black text-[20px] mb-2 font-[600]">
      User Tracker
    </div>

    <SummaryCard
      :summary-data="summaryData"
    />

    <div class="flex flex-wrap justify-content-end align-items-center gap-5">
      <date-range-picker
        ref="picker"
        v-model="dateRange"
        :locale-data="locale"
        :ranges="ranges"
        :opens="'auto'"
        control-container-class="border rounded"
      >
        <template v-slot:input="picker">
          <div
            class="d-flex btn justify-content-between align-items-center gap-5"
            style="height: 38px;"
          >
            <span class="d-flex justify-content-start">
              {{ getRangeTitle(picker.startDate, picker.endDate) }}
            </span>
            <img
              src="https://storage.googleapis.com/komerce/assets/komerce-icon/Hitam/arrow-down-1.svg"
              alt="arrow-down"
              width="15px"
              class="d-flex justify-content-end"
            >
          </div>
        </template>
      </date-range-picker>

      <VSelect
        v-model="statusRequest"
        :reduce="e => e.value"
        :options="optionsStatus"
        label="text"
        :clearable="false"
        class="font-semibold !text-[#333] sm:w-100 lg:w-[170px]"
        @input="limit = 20, offset = 0, currentPage = 1, perPage = 20, totalPerPage = 20, getListDoc()"
      />

      <div class="position-relative">
        <div
          class="d-flex justify-content-between align-items-center border rounded cursor-pointer sm:w-100 lg:w-[170px] px-[10px]"
          style="height: 38px;"
          @click="toggleServiceDropdown"
        >
          <span class="font-semibold text-[#333] text-[14px]">{{ serviceDropdownLabel }}</span>
          <img
            src="https://storage.googleapis.com/komerce/assets/komerce-icon/Hitam/arrow-down-1.svg"
            alt="arrow-down"
            width="15px"
          >
        </div>
        <div
          v-if="isServiceDropdownOpen"
          class="position-absolute bg-white z-10 border rounded shadow-lg mt-1 py-1"
          style="min-width: 200px;"
        >
          <div
            v-for="(item, index) in optionsServices"
            :key="index"
            class="p-1"
          >
            <b-form-checkbox
              v-model="item.checked"
              class="custom-control-primary"
              @change="onServiceCheckChange(item, index)"
            >
              <span class="text-black text-[14px]">{{ item.text }}</span>
            </b-form-checkbox>
          </div>
        </div>
      </div>

      <b-form-group class="mb-0 text-end d-flex flex-row align-items-center">
        <div class="d-flex gap-5">
          <b-input-group
            class="input-group-merge"
            size="md"
          >
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input
              id="filterInput"
              v-model="search"
              :maxlength="32"
              :minlength="2"
              type="search"
              placeholder="Search Collaborator"
              debounce="500"
              @input="onChangeSearch"
            />
          </b-input-group>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            size="md"
            class="text-nowrap"
            @click="resetFilter()"
          >
            Reset Filter
          </b-button>
        </div>
      </b-form-group>
    </div>

    <BOverlay
      :show="loading"
      spinner-variant="primary"
      variant="light"
      blur="0"
      opacity=".5"
      rounded="sm"
    >
      <BTable
        id="scroll"
        ref="table-scroll"
        :items="listCollaborator"
        :fields="fieldsRequestDoc"
        head-row-variant="d-none p-0"
        show-empty
        empty-text="Tidak ada data yang ditampilkan."
        responsive
        sticky-header="500px"
        class="mt-1"
        borderless
      >
        <template #thead-top>
          <tr>
            <th
              v-for="col in mainColsUserTracker"
              :key="col.label"
              :rowspan="col.rowspan || null"
              :colspan="col.colspan || null"
              class="!text-black !capitalize !text-[13px] font-medium py-[5px] px-1 align-middle whitespace-nowrap"
              :class="col.class"
            >
              {{ col.label }}
            </th>
          </tr>
          <tr>
            <th
              v-for="sub in subColsUserTracker"
              :key="sub"
              class="!text-black !capitalize !text-[13px] font-medium px-0 text-center sticky-sub"
            >
              {{ sub }}
            </th>
          </tr>
        </template>

        <template
          v-for="field in fieldsRequestDoc"
          #[`head(${field.key})`]="data"
        >
          <div
            :key="field.key"
            class="d-none"
          >
            {{ data }}
          </div>
        </template>

        <template #cell(name)="data">
          <div class="d-flex flex-column">
            {{ data.item.name }}
            <small class="text-secondary">{{ data.item.email }}</small>
            <small
              role="button"
              class="text-secondary"
            >{{ formatNoPhone(data.item.contact) }}</small>
          </div>
        </template>

        <template #cell(status)="data">
          <div
            :class="getStatusClass(data.item.current_status.toLowerCase())"
            class="rounded-pill text-center py-[5px] px-[1px] text-sm capitalize"
            style="width: 100px;"
          >
            {{ data.item.current_status }}
          </div>
        </template>

        <template #cell(service)="data">
          <div class="d-flex flex-column min-w-[20px]">
            <ul
              v-for="(service, index) in data.item.services"
              :key="index"
              class="list-disc p-0 m-0"
            >
              <li class="text-sm text-secondary">
                {{ service }}
              </li>
            </ul>
            <span v-if="data.item.services.length === 0">-</span>
          </div>
        </template>

        <template #cell(sla)="data">
          <div class="d-flex justify-content-center align-items-center">
            <img
              :src="statusIcon(data.item.document_sla_status)"
              alt="status"
            >
          </div>
        </template>

        <template #cell(ca)="data">
          <div class="d-flex justify-content-center align-items-center">
            <img
              :src="statusIcon(data.item.document_ca_status)"
              alt="status"
            >
          </div>
        </template>

        <template #cell(uat)="data">
          <div class="d-flex justify-content-center align-items-center">
            <img
              :src="statusIcon(data.item.uat_status)"
              alt="status"
            >
          </div>
        </template>

        <template #cell(detail)="data">
          <div class="d-flex align-items-center gap-5 detail">
            <span
              class="cursor-pointer text-[#08A0F7]"
              @click="navigateToDetail(data.item.user_id)"
            >
              Details
            </span>
          </div>
        </template>
      </BTable>
    </BOverlay>
    <BRow>
      <BCol
        cols="12"
        class="d-flex justify-content-between"
      >
        <div
          class="bg-light d-flex justify-content-center align-items-center p-50 rounded"
        >
          <span class="text-black mr-50">Items per page :  </span>
          <BButton
            v-for="page in optionsPage"
            :key="page"
            class="btn-icon"
            size="sm"
            :variant="totalPerPage === page ? 'primary' : 'flat-dark'"
            @click="handlePerPage(page)"
          >
            {{ page }}
          </BButton>
        </div>

        <BPagination
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          aria-controls="scroll"
          first-number
          last-number
          @input="handlePagination"
        />
      </BCol>
    </BRow>
  </BCard>
</template>

<script>
import Ripple from 'vue-ripple-directive'
import { openApiAxiosIns, collaboratorAxiosIns } from '@/libs/axios'
import {
  LABELDAY,
  LABELMONTH,
  YEAR_MONTH_DAY,
} from '@/libs/filterDate'
import moment from 'moment'
import {
  today,
  last7,
  last30,
  firstDateOfMonth,
  lastDateOfMonth,
  lastMonth,
  lastDateOfLastMonth,
} from '@/store/helpers'
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import VSelect from 'vue-select'
import '@core/scss/vue/libs/vue-select.scss'
import { fieldsRequestDoc, mainColsUserTracker, subColsUserTracker } from '../config'
import SummaryCard from './SummaryCard.vue'

export default {
  directives: { Ripple },
  components: {
    DateRangePicker,
    VSelect,
    SummaryCard,
  },
  data() {
    return {
      loading: false,
      search: '',
      fieldsRequestDoc,
      mainColsUserTracker,
      subColsUserTracker,
      YEAR_MONTH_DAY,
      picker: {
        startDate: today,
        endDate: today,
      },
      locale: {
        format: 'dd/mm/yyyy',
        daysOfWeek: LABELDAY,
        monthNames: LABELMONTH,
        cancelLabel: 'Back',
      },
      ranges: {
        Today: [today, today],
        'Last 7 Days': [last7, today],
        'Last 1 Month': [lastMonth(1), lastDateOfLastMonth],
        'This Month': [firstDateOfMonth, lastDateOfMonth],
        'Custom Range': [today, today],
      },
      today,
      last7,
      last30,
      firstDateOfMonth,
      lastDateOfMonth,
      dateRange: {
        startDate: today,
        endDate: today,
      },
      statusRequest: 'all',
      optionsStatus: [
        { text: 'All Status', value: 'all' },
        { text: 'Submitted', value: 'submitted' },
        { text: 'Need Revision', value: 'need_revision' },
        { text: 'Live', value: 'live' },
      ],
      activity: 'all_activity',
      isServiceDropdownOpen: false,
      optionsServices: [
        { text: 'All Services', value: 'all', checked: true },
        { text: 'Shipping Delivery', value: 'shipping_delivery', checked: true },
        { text: 'Payment Service', value: 'payment_service', checked: true },
        { text: 'Qrisly', value: 'qrisly', checked: true },
      ],
      listCollaborator: [],
      rows: 0,
      limit: 20,
      offset: 0,
      lastData: false,
      isWAValid: true,
      optionsPage: [20, 40, 60, 80, 100],
      totalPerPage: 20,
      perPage: 20,
      currentPage: 1,
      summaryData: {
        totalUser: 0,
        submitted: 0,
        needRevision: 0,
        live: 0,
      },
    }
  },
  computed: {
    serviceDropdownLabel() {
      const checkedServices = this.optionsServices.slice(1).filter(opt => opt.checked)
      if (checkedServices.length === 0) return 'All Services'
      if (checkedServices.length === this.optionsServices.length - 1) return 'All Services'
      if (checkedServices.length === 1) return checkedServices[0].text
      return `${checkedServices.length} Services`
    },
    selectedServicesParam() {
      const checkedServices = this.optionsServices.slice(1).filter(opt => opt.checked)
      if (checkedServices.length === 0 || checkedServices.length === this.optionsServices.length - 1) return ''
      return checkedServices.map(opt => opt.value).join(',')
    },
  },
  watch: {
    dateRange: {
      handler() {
        this.offset = 0
        this.limit = 20
        this.currentPage = 1
        this.perPage = 20
        this.totalPerPage = 20
        this.getListDoc()
        this.getListSummary()
      },
    },
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutsideService)
    this.getListDoc(true)
    this.getListSummary()
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutsideService)
  },
  methods: {
    handleClickOutsideService(event) {
      const dropdown = this.$el.querySelector('.position-relative')
      if (dropdown && !dropdown.contains(event.target)) {
        this.isServiceDropdownOpen = false
      }
    },
    toggleServiceDropdown() {
      this.isServiceDropdownOpen = !this.isServiceDropdownOpen
    },
    onServiceCheckChange(item, index) {
      if (index === 0) {
        // "All Services" always re-checks all
        this.optionsServices = this.optionsServices.map(opt => ({
          ...opt,
          checked: true,
        }))
      } else {
        // Check if no individual service is checked → reset to all
        const anyChildChecked = this.optionsServices.slice(1).some(opt => opt.checked)
        if (!anyChildChecked) {
          this.optionsServices = this.optionsServices.map(opt => ({
            ...opt,
            checked: true,
          }))
        } else {
          const allChildrenChecked = this.optionsServices.slice(1).every(opt => opt.checked)
          this.$set(this.optionsServices[0], 'checked', allChildrenChecked)
        }
      }
      this.offset = 0
      this.limit = 20
      this.currentPage = 1
      this.perPage = 20
      this.totalPerPage = 20
      this.getListDoc()
    },
    formatDate(d) {
      if (d) return moment(d).format('DD MMMM YYYY')
      return '-'
    },
    navigateToDetail(v) {
      this.$router.push({ path: `/openapi/user-tracker/detail/${v}` })
    },
    onChangeSearch: _.debounce(function search() {
      this.offset = 0
      this.limit = 20
      this.currentPage = 1
      this.perPage = 20
      this.totalPerPage = 20
      const trimmedSearch = this.search.trim()
      this.search = trimmedSearch

      if (trimmedSearch.length > 2 || trimmedSearch === '') {
        this.getListDoc()
      }
    }, 1000),
    formatDateParams(d) {
      return moment(d).format('YYYY-MM-DD')
    },
    getRangeTitle(start, end) {
      const s = moment(start).format('YYYY-MM-DD')
      const e = moment(end).format('YYYY-MM-DD')

      const match = Object.keys(this.ranges).find(title => {
        const r = this.ranges[title]
        return (
          moment(r[0]).format('YYYY-MM-DD') === s
          && moment(r[1]).format('YYYY-MM-DD') === e
        )
      })

      return (
        match
        || `${moment(start).format('DD MMM YYYY')} - ${moment(end).format('DD MMM YYYY')}`
      )
    },
    formatNoPhone(e) {
      if (e.startsWith('0')) {
        return e.replace(/^0/, '+62')
      } if (e.startsWith('8')) {
        return e.replace('8', '+628')
      }
      return e
    },
    handlePagination(val) {
      this.currentPage = val
      this.offset = (val - 1) * this.perPage
      this.getListDoc()
    },
    handlePerPage(page) {
      this.totalPerPage = page
      this.perPage = page
      this.limit = page
      this.currentPage = 1
      this.offset = 0
      this.getListDoc()
    },
    async getListDoc(isInitial = false) {
      const storageKey = 'params-user-tracker-ro'
      let params = {}

      if (isInitial) {
        const savedParams = localStorage.getItem(storageKey)
        if (savedParams) {
          try {
            params = JSON.parse(savedParams)
            this.limit = params.limit ?? this.limit
            this.offset = params.offset ?? this.offset
            this.perPage = this.limit
            this.totalPerPage = this.limit
            this.currentPage = Math.floor(this.offset / this.limit) + 1
            this.search = params.search ?? this.search
            this.dateRange.startDate = params.start_date ? new Date(params.start_date) : this.dateRange.startDate
            this.dateRange.endDate = params.end_date ? new Date(params.end_date) : this.dateRange.endDate
            this.statusRequest = params.current_status ?? this.statusRequest
            this.activity = params.last_activity ?? this.activity
            if (params.service) {
              const services = params.service.split(',')
              this.optionsServices = this.optionsServices.map((opt, index) => {
                if (index === 0) return { ...opt, checked: false }
                return { ...opt, checked: services.includes(opt.value) }
              })
            }
          } catch (e) {
            console.warn('Failed to parse saved params:', e)
          }
        }
      }

      params = {
        limit: this.limit,
        offset: this.offset,
        ...(this.search ? { search: this.search } : {}),
        start_date: this.formatDateParams(this.dateRange.startDate),
        end_date: this.formatDateParams(this.dateRange.endDate),
        current_status: this.statusRequest,
        last_activity: this.activity,
        ...(this.selectedServicesParam ? { service: this.selectedServicesParam } : {}),
      }

      localStorage.setItem(storageKey, JSON.stringify(params))

      this.loading = true
      const scrollContainer = this.$refs['table-scroll']?.$el
      const url = '/user/api/v2/admin/document-request'

      try {
        const res = await openApiAxiosIns.get(url, { params })
        const { list, count } = res.data.data
        this.listCollaborator = list
        this.rows = count
        this.loading = false

        if (scrollContainer) {
          scrollContainer.scrollTop = 0
        }

        this.lastData = count < this.limit
      } catch (error) {
        this.loading = false
        if (!this.lastData) {
          this.$toast_error({ message: 'Failed to retrieve data' })
        }
      }
    },

    resetFilter() {
      this.search = ''
      this.statusRequest = 'all'
      this.dateRange.startDate = firstDateOfMonth
      this.dateRange.endDate = lastDateOfMonth
      this.activity = 'all_activity'
      this.optionsServices = this.optionsServices.map(opt => ({
        ...opt,
        checked: true,
      }))
      this.limit = 20
      this.offset = 0
      this.perPage = 20
      this.totalPerPage = 20
      this.currentPage = 1
      this.getListDoc()
      this.getListSummary()
    },
    statusIcon(v) {
      // 1: not started, 2: in progress, 3: need revision, 4: submitted, 5: live, 6: rejected
      if (v <= 3) return 'https://storage.googleapis.com/komerce/icon/circle-minus-gray.svg'
      if (v === 4) return 'https://storage.googleapis.com/komerce/assets/icons/minus-cirlce.svg'
      if (v === 5) return 'https://storage.googleapis.com/komerce/assets/icons/Checklist-fill.svg'
      if (v === 6) return 'https://storage.googleapis.com/komerce/assets/LP-Komads/Symbol%20close.svg'
      return ''
    },
    getStatusClass(v) {
      if (v === 'active') return 'live'
      if (v === 'live') return 'live'
      if (v === 'non active') return 'non-actived'
      if (v === 'need revision') return 'rejected'
      if (v === 'non activated') return 'non-actived'
      if (v === 'not started') return 'not-started'
      if (v === 'submitted') return 'submitted'
      if (v === 'approved') return 'approved'
      if (v === 'rejected') return 'rejected'
      return 'not-started'
    },

    getListSummary() {
      const params = {
        start_date: this.formatDateParams(this.dateRange.startDate),
        end_date: this.formatDateParams(this.dateRange.endDate),
      }
      try {
        openApiAxiosIns.get('/user/api/v1/admin/users/tracker', { params }).then(response => {
          const { data } = response.data
          this.summaryData = {
            totalUser: data.total_user,
            submitted: data.submitted,
            needRevision: data.need_revision,
            live: data.live,
          }
        })
      } catch (error) {
        this.$toast_error({ message: 'Failed to retrieve summary data' })
      }
    },
  },
}
</script>
<style scoped>
.decreased {
    background-color: #FCBEBE;
    color: #E31A1A;
    width: fit-content;
}
.increased {
    background-color: #BEFCDE;
    color: #34A770;
    width: fit-content;
}
.submitted {
  background-color: #fffbe6;
  border: 1px solid #faad15;
  color: #AF6A13;
}
.approved {
  background-color: #eaffe6;
  border: 1px solid #32a46e;
  color: #32a46e;
}
.rejected {
  background-color: #ffe6e6;
  border: 1px solid #d22120;
  color: #d22120;
}
.live {
    background-color: #BEFCDE;
    color: #34A770;
    width: 100px;
    border: 1px solid #34A770;
}
.non-actived {
    background-color: #FCBEBE;
    color: #E31A1A;
    width: 100px;
    border: 1px solid #E31A1A;
}
.revision {
    background-color: #FFF2E2;
    color: #AF6A13;
    width: 100px;
    border: 1px solid #FBA63C;
}
.not-started {
    background-color: #E2E2E2;
    color: #828282;
    width: 100px;
    border: 1px solid #828282;
}

.table.b-table thead tr:first-child th {
  position: sticky;
  top: 0;
  z-index: 2;
}

.table.b-table thead tr:nth-child(2) th.sticky-sub {
  position: sticky;
  top: 38px;
  z-index: 1;
}
</style>
